import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter,
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Eye, 
  Copy, 
  MoreVertical, 
  AlertCircle,
  Search, 
  Clock,
  RefreshCw,
  Hourglass,
  ChevronDown,
  ChevronRight,
  Terminal,
  FileText,
  Maximize2
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { TimeWithTimezone, TimezoneContext } from '@/components/timezone';

// Types
interface LlmLog {
  id: string;
  created_at: string;
  prompt_name: string | null;
  user_id: string | null;
  brand_id: string | null;
  model_name: string;
  request_messages: any;
  response_body: any;
  tokens_used: number | null;
  error_message: string | null;
  duration_ms: number | null;
  cost: number | null;
}

// JSON Tree Component
interface JsonTreeProps {
  data: any;
  level?: number;
  onViewLongText?: (text: string, path: string) => void;
}

const JsonTree = ({ data, level = 0, onViewLongText }: JsonTreeProps) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  
  const toggleExpanded = (key: string) => {
    const newExpanded = new Set(expandedKeys);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedKeys(newExpanded);
  };

  const renderValue = (value: any, path: string): JSX.Element => {
    if (value === null) return <span className="text-orange-600">null</span>;
    if (value === undefined) return <span className="text-orange-600">undefined</span>;
    
    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-600' : 'text-red-600'}>{String(value)}</span>;
    }
    
    if (typeof value === 'number') {
      return <span className="text-blue-600">{value}</span>;
    }
    
    if (typeof value === 'string') {
      // Check if it's a long text (more than 100 chars or has newlines)
      const isLongText = value.length > 100 || value.includes('\n');
      
      if (isLongText) {
        const preview = value.substring(0, 80).replace(/\n/g, ' ') + '...';
        return (
          <span className="inline-flex items-center gap-2">
            <span className="text-green-600">"{preview}"</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => onViewLongText?.(value, path)}
            >
              <Maximize2 className="h-3 w-3 mr-1" />
              View
            </Button>
          </span>
        );
      }
      
      // Try to detect if it's JSON string
      if ((value.startsWith('{') && value.endsWith('}')) || (value.startsWith('[') && value.endsWith(']'))) {
        try {
          const parsed = JSON.parse(value);
          return (
            <span className="inline-flex items-center gap-2">
              <span className="text-green-600">"{value.substring(0, 30)}..."</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => {
                  const formatted = JSON.stringify(parsed, null, 2);
                  onViewLongText?.(formatted, path + ' (parsed JSON)');
                }}
              >
                <FileText className="h-3 w-3 mr-1" />
                JSON
              </Button>
            </span>
          );
        } catch {
          // Not valid JSON, render as normal string
        }
      }
      
      return <span className="text-green-600">"{value}"</span>;
    }
    
    if (Array.isArray(value)) {
      return <span className="text-muted-foreground">[{value.length} items]</span>;
    }
    
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      return <span className="text-muted-foreground">{`{${keys.length} properties}`}</span>;
    }
    
    return <span className="text-muted-foreground">{String(value)}</span>;
  };

  const renderTree = (obj: any, parentPath: string = ''): JSX.Element[] => {
    if (obj === null || obj === undefined) {
      return [];
    }

    if (Array.isArray(obj)) {
      return obj.map((item, index) => {
        const key = `${parentPath}[${index}]`;
        const isExpandable = typeof item === 'object' && item !== null;
        const isExpanded = expandedKeys.has(key);
        
        return (
          <div key={key} className="font-mono text-sm">
            <div 
              className={cn(
                "flex items-start py-1 hover:bg-muted/50 rounded px-2 -mx-2",
                isExpandable && "cursor-pointer"
              )}
              style={{ paddingLeft: `${level * 20}px` }}
              onClick={() => isExpandable && toggleExpanded(key)}
            >
              {isExpandable && (
                <ChevronRight 
                  className={cn(
                    "h-4 w-4 mr-1 mt-0.5 transition-transform flex-shrink-0",
                    isExpanded && "rotate-90"
                  )} 
                />
              )}
              {!isExpandable && <span className="w-5" />}
              <span className="text-muted-foreground mr-2">[{index}]:</span>
              {!isExpandable && renderValue(item, key)}
            </div>
            {isExpandable && isExpanded && (
              <div>
                {renderTree(item, key).map((child, i) => (
                  <div key={i}>{child}</div>
                ))}
              </div>
            )}
          </div>
        );
      });
    }

    if (typeof obj === 'object') {
      return Object.entries(obj).map(([key, value]) => {
        const fullKey = parentPath ? `${parentPath}.${key}` : key;
        const isExpandable = typeof value === 'object' && value !== null;
        const isExpanded = expandedKeys.has(fullKey);
        
        return (
          <div key={fullKey} className="font-mono text-sm">
            <div 
              className={cn(
                "flex items-start py-1 hover:bg-muted/50 rounded px-2 -mx-2",
                isExpandable && "cursor-pointer"
              )}
              style={{ paddingLeft: `${level * 20}px` }}
              onClick={() => isExpandable && toggleExpanded(fullKey)}
            >
              {isExpandable && (
                <ChevronRight 
                  className={cn(
                    "h-4 w-4 mr-1 mt-0.5 transition-transform flex-shrink-0",
                    isExpanded && "rotate-90"
                  )} 
                />
              )}
              {!isExpandable && <span className="w-5" />}
              <span className="text-purple-600 mr-2">{key}:</span>
              {renderValue(value, fullKey)}
            </div>
            {isExpandable && isExpanded && (
              <div>
                {renderTree(value, fullKey).map((child, i) => (
                  <div key={i}>{child}</div>
                ))}
              </div>
            )}
          </div>
        );
      });
    }

    return [];
  };

  return <div className="py-2">{renderTree(data)}</div>;
};

// Syntax Highlighter Component
interface SyntaxHighlighterProps {
  text: string;
}

const SyntaxHighlighter = ({ text }: SyntaxHighlighterProps) => {
  // Color arrays for different depths/levels
  const xmlColors = [
    'text-blue-600',
    'text-green-600', 
    'text-purple-600',
    'text-red-600',
    'text-yellow-600',
    'text-cyan-600',
    'text-pink-600',
    'text-indigo-600'
  ];

  const headingColors = [
    'text-red-700 font-bold text-lg',      // #
    'text-orange-600 font-semibold text-base', // ##
    'text-yellow-600 font-medium',         // ###
    'text-green-600 font-medium',          // ####
    'text-blue-600',                       // #####
    'text-purple-600'                      // ######
  ];

  const highlightText = (content: string): JSX.Element[] => {
    const lines = content.split('\n');
    const result: JSX.Element[] = [];
    
    lines.forEach((line, lineIndex) => {
      const elements: JSX.Element[] = [];
      
      // Check for markdown headers first
      const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const headerText = headerMatch[2];
        const colorClass = headingColors[level - 1] || headingColors[5];
        
        elements.push(
          <span key={`header-${lineIndex}`} className={colorClass}>
            <span className="text-gray-500">{headerMatch[1]}</span> {headerText}
          </span>
        );
      } else {
        // Process XML tags and regular content
        let currentPos = 0;
        let xmlDepth = 0;
        
        // Simple XML tag regex
        const xmlRegex = /<\/?[^>]+>/g;
        let match;
        
        const lineParts: { text: string; isXml: boolean; depth: number }[] = [];
        
        while ((match = xmlRegex.exec(line)) !== null) {
          // Add text before tag
          if (match.index > currentPos) {
            lineParts.push({
              text: line.slice(currentPos, match.index),
              isXml: false,
              depth: 0
            });
          }
          
          // Determine if opening or closing tag
          const tag = match[0];
          const isClosing = tag.startsWith('</');
          const isSelfClosing = tag.endsWith('/>');
          
          if (isClosing) {
            xmlDepth = Math.max(0, xmlDepth - 1);
          }
          
          lineParts.push({
            text: tag,
            isXml: true,
            depth: xmlDepth
          });
          
          if (!isClosing && !isSelfClosing) {
            xmlDepth++;
          }
          
          currentPos = match.index + match[0].length;
        }
        
        // Add remaining text
        if (currentPos < line.length) {
          lineParts.push({
            text: line.slice(currentPos),
            isXml: false,
            depth: 0
          });
        }
        
        // Render the parts
        lineParts.forEach((part, partIndex) => {
          if (part.isXml) {
            const colorClass = xmlColors[part.depth % xmlColors.length];
            elements.push(
              <span key={`xml-${lineIndex}-${partIndex}`} className={`${colorClass} font-semibold`}>
                {part.text}
              </span>
            );
          } else {
            elements.push(
              <span key={`text-${lineIndex}-${partIndex}`}>
                {part.text}
              </span>
            );
          }
        });
      }
      
      result.push(
        <div key={lineIndex} className="leading-relaxed">
          {elements.length > 0 ? elements : <span>{line}</span>}
        </div>
      );
    });
    
    return result;
  };

  return (
    <div className="text-sm font-mono whitespace-pre-wrap break-words">
      {highlightText(text)}
    </div>
  );
};

// Text Viewer Dialog Component
interface TextViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  text: string;
  title: string;
}

const TextViewerDialog = ({ open, onOpenChange, text, title }: TextViewerDialogProps) => {
  const copyToClipboard = (content: string) => {
    navigator.clipboard.writeText(content)
      .then(() => {
        toast({
          title: 'Copied to clipboard',
          description: 'Content has been copied to clipboard',
          duration: 2000,
        });
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        toast({
          title: 'Copy failed',
          description: 'Failed to copy to clipboard',
          variant: 'destructive',
        });
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-10">
            <span>Text Viewer</span>
            <Badge variant="outline" className="ml-2 font-normal">{title}</Badge>
          </DialogTitle>
          <DialogDescription>
            Viewing formatted text content
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 flex flex-col gap-2 min-h-0">
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => copyToClipboard(text)}
            >
              <Copy className="h-3 w-3 mr-2" />
              Copy
            </Button>
          </div>
          
          <div className="border rounded-md bg-muted/50" style={{ height: 'calc(90vh - 200px)' }}>
            <div className="h-full overflow-y-auto p-4">
              <SyntaxHighlighter text={text} />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Function to format JSON for display
const formatJson = (json: any): string => {
  if (json === null || json === undefined) {
    return "null";
  }
  
  try {
    return JSON.stringify(json, null, 2);
  } catch (e) {
    // If error in stringify with formatting, try without formatting
    try {
      return JSON.stringify(json);
    } catch (e) {
      return String(json);
    }
  }
};

export default function LlmLogs() {
  const [logs, setLogs] = useState<LlmLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState<LlmLog | null>(null);
  const [viewMode, setViewMode] = useState<'request' | 'response'>('request');
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [displayMode, setDisplayMode] = useState<'tree' | 'raw'>('tree');
  const [textViewerContent, setTextViewerContent] = useState<{ text: string; title: string } | null>(null);

  // Function to load logs
  const loadLogs = async () => {
    try {
      setLoading(true);
      
      // Get total count for pagination
      const { count, error: countError } = await supabase
        .from('llm_logs')
        .select('id', { count: 'exact', head: true })
        .ilike('prompt_name', searchQuery ? `%${searchQuery}%` : '%');
      
      if (countError) throw countError;
      setTotal(count || 0);
      
      // Get logs with pagination
      const { data, error } = await supabase
        .from('llm_logs')
        .select('*')
        .ilike('prompt_name', searchQuery ? `%${searchQuery}%` : '%')
        .order('created_at', { ascending: false })
        .range((page - 1) * perPage, page * perPage - 1);
      
      if (error) throw error;
      setLogs(data || []);

    } catch (error) {
      console.error('Error loading LLM logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load LLM logs. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Load logs on page load and when pagination or search changes
  useEffect(() => {
    loadLogs();
  }, [page, perPage, searchQuery]);

  // Handle refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
    loadLogs();
  };

  // Copy to clipboard function
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast({
          title: 'Copied to clipboard',
          description: 'Content has been copied to clipboard',
          duration: 2000,
        });
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        toast({
          title: 'Copy failed',
          description: 'Failed to copy to clipboard',
          variant: 'destructive',
        });
      });
  };

  // Format duration for display
  const formatDuration = (ms: number | null | undefined): string => {
    if (ms === null || ms === undefined) return '—';
    
    if (ms < 1000) return `${ms}ms`;
    
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(2)}s`;
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  };

  // Calculate total pages
  const totalPages = Math.ceil(total / perPage);

  // Render pagination items
  const renderPaginationItems = () => {
    const items = [];
    
    // Previous page
    items.push(
      <PaginationItem key="prev">
        <PaginationPrevious 
          onClick={() => page > 1 && setPage(page - 1)} 
          isActive={page > 1}
          className={page === 1 ? 'pointer-events-none opacity-50' : ''}
        />
      </PaginationItem>
    );
    
    // First page
    items.push(
      <PaginationItem key="first">
        <PaginationLink
          onClick={() => setPage(1)}
          isActive={page === 1}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );
    
    // Ellipsis after first page
    if (page > 3) {
      items.push(
        <PaginationItem key="ellipsis1">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Pages around current page
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => setPage(i)}
            isActive={page === i}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    // Ellipsis before last page
    if (page < totalPages - 2) {
      items.push(
        <PaginationItem key="ellipsis2">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Last page (if more than one page)
    if (totalPages > 1) {
      items.push(
        <PaginationItem key="last">
          <PaginationLink
            onClick={() => setPage(totalPages)}
            isActive={page === totalPages}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    // Next page
    items.push(
      <PaginationItem key="next">
        <PaginationNext 
          onClick={() => page < totalPages && setPage(page + 1)}
          isActive={page < totalPages}
          className={page === totalPages ? 'pointer-events-none opacity-50' : ''}
        />
      </PaginationItem>
    );
    
    return items;
  };

  return (
    <div className="space-y-4">
      {/* Timezone Context */}
      <TimezoneContext 
        placement="inline" 
        compact={true}
        className="mb-4"
      />
      
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">LLM Logs</h2>
        
        <div className="flex w-full sm:w-auto gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by prompt name..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button 
            onClick={handleRefresh} 
            variant="outline"
            className="flex-shrink-0"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex-shrink-0">
                {perPage} per page <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {[10, 20, 50, 100].map((value) => (
                <DropdownMenuItem
                  key={value}
                  onClick={() => {
                    setPerPage(value);
                    setPage(1); // Reset to first page
                  }}
                >
                  {value} per page
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>LLM API Call Logs</CardTitle>
          <CardDescription>
            Recent LLM API calls with detailed request and response data
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="w-full h-16" />
              ))}
            </div>
          ) : logs.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Prompt</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Duration</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs">
                        <TimeWithTimezone 
                          date={log.created_at}
                          showTimezone={false}
                          size="xs"
                          showTooltip={true}
                          showRelative={true}
                          relativeMode="smart"
                          preferRelative={true}
                          relativeOptions={{
                            shortFormat: true,
                            absoluteThreshold: 1
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{log.prompt_name || 'Unknown Prompt'}</span>
                          {log.brand_id && (
                            <span className="text-xs text-muted-foreground">Brand ID: {log.brand_id.substring(0, 8)}...</span>
                          )}
                          {log.user_id && (
                            <span className="text-xs text-muted-foreground">User ID: {log.user_id.substring(0, 8)}...</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.model_name}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {log.tokens_used || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span>{formatDuration(log.duration_ms)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {log.cost 
                          ? `$${log.cost.toFixed(6)}` 
                          : '—'
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        {log.error_message ? (
                          <Badge variant="destructive">Error</Badge>
                        ) : (
                          <Badge variant="success">Success</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  copyToClipboard(formatJson(log.request_messages));
                                }}
                              >
                                <Copy className="mr-2 h-4 w-4" />
                                Copy Request
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  copyToClipboard(formatJson(log.response_body));
                                }}
                              >
                                <Copy className="mr-2 h-4 w-4" />
                                Copy Response
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <Terminal className="h-10 w-10 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No LLM Logs Found</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                {searchQuery 
                  ? `No logs found matching "${searchQuery}". Try a different search term or clear the search.`
                  : "No LLM API call logs found. Logs will appear here when API calls are made."}
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <div className="text-sm text-muted-foreground">
            {total > 0 ? (
              <>Showing {(page - 1) * perPage + 1}-{Math.min(page * perPage, total)} of {total} logs</>
            ) : (
              'No logs found'
            )}
          </div>
          
          <Pagination>
            <PaginationContent>
              {renderPaginationItems()}
            </PaginationContent>
          </Pagination>
        </CardFooter>
      </Card>

      {/* LLM Log Details Dialog */}
      {selectedLog && (
        <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col min-h-0">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  <span>LLM Log Details</span>
                  {selectedLog.error_message && (
                    <Badge variant="destructive" className="ml-2">Error</Badge>
                  )}
                </DialogTitle>
                <Badge variant="outline">{selectedLog.model_name}</Badge>
              </div>
              <DialogDescription>
                <div className="flex flex-col gap-1 mt-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Prompt:</span>
                    <span>{selectedLog.prompt_name || 'Unknown'}</span>
                  </div>
                  {selectedLog.brand_id && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Brand ID:</span>
                      <span className="font-mono text-xs">{selectedLog.brand_id}</span>
                    </div>
                  )}
                  {selectedLog.user_id && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">User ID:</span>
                      <span className="font-mono text-xs">{selectedLog.user_id}</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>Created: </span>
                      <TimeWithTimezone 
                        date={selectedLog.created_at}
                        showTimezone={true}
                        size="xs"
                        showTooltip={true}
                        showRelative={true}
                        relativeMode="contextual"
                        relativeContext="detailed"
                        preferRelative={false}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Hourglass className="h-3 w-3" />
                      <span>Duration: {formatDuration(selectedLog.duration_ms)}</span>
                    </div>
                    {selectedLog.tokens_used && (
                      <div>Tokens: {selectedLog.tokens_used.toLocaleString()}</div>
                    )}
                    {selectedLog.cost && (
                      <div>Cost: ${selectedLog.cost.toFixed(6)}</div>
                    )}
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>

            {/* Tabs for viewing content */}
            <Tabs 
              defaultValue="request" 
              value={viewMode} 
              onValueChange={(value) => setViewMode(value as 'request' | 'response')}
              className="flex-1 flex flex-col min-h-0"
            >
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <TabsList className="grid grid-cols-2">
                  <TabsTrigger value="request">Request</TabsTrigger>
                  <TabsTrigger value="response">Response</TabsTrigger>
                </TabsList>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant={displayMode === 'tree' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDisplayMode('tree')}
                  >
                    Tree View
                  </Button>
                  <Button
                    variant={displayMode === 'raw' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDisplayMode('raw')}
                  >
                    Raw JSON
                  </Button>
                </div>
              </div>

              <TabsContent value="request" className="flex-1 overflow-hidden min-h-0">
                <div className="h-full flex flex-col">
                  {selectedLog.error_message && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex-shrink-0">
                      <div className="flex gap-2 items-start">
                        <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-red-700">Error Message</h4>
                          <p className="text-sm text-red-600">{selectedLog.error_message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="border rounded-md p-4 h-full overflow-hidden flex flex-col min-h-0">
                    <div className="flex justify-end mb-2 flex-shrink-0">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(formatJson(selectedLog.request_messages))}
                      >
                        <Copy className="h-3 w-3 mr-2" />
                        Copy
                      </Button>
                    </div>

                    <div className="flex-1 min-h-0 overflow-hidden">
                      <ScrollArea className="h-full">
                        {displayMode === 'tree' ? (
                          <JsonTree 
                            data={selectedLog.request_messages} 
                            onViewLongText={(text, path) => setTextViewerContent({ text, title: path })}
                          />
                        ) : (
                          <pre className="text-xs font-mono">
                            {formatJson(selectedLog.request_messages)}
                          </pre>
                        )}
                      </ScrollArea>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="response" className="flex-1 overflow-hidden min-h-0">
                <div className="h-full flex flex-col">
                  <div className="border rounded-md p-4 h-full overflow-hidden flex flex-col min-h-0">
                    <div className="flex justify-end mb-2 flex-shrink-0">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(formatJson(selectedLog.response_body))}
                      >
                        <Copy className="h-3 w-3 mr-2" />
                        Copy
                      </Button>
                    </div>

                    <div className="flex-1 min-h-0 overflow-hidden">
                      <ScrollArea className="h-full">
                        {displayMode === 'tree' ? (
                          <JsonTree 
                            data={selectedLog.response_body} 
                            onViewLongText={(text, path) => setTextViewerContent({ text, title: path })}
                          />
                        ) : (
                          <pre className="text-xs font-mono">
                            {formatJson(selectedLog.response_body)}
                          </pre>
                        )}
                      </ScrollArea>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-4">
              <Button onClick={() => setSelectedLog(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Text Viewer Dialog */}
      {textViewerContent && (
        <TextViewerDialog
          open={!!textViewerContent}
          onOpenChange={(open) => !open && setTextViewerContent(null)}
          text={textViewerContent.text}
          title={textViewerContent.title}
        />
      )}
    </div>
  );
}